import { Fragment } from "react";

// ui
import {
  Typography,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Alert,
  Checkbox,
  TextField,
} from "@mui/material";
// ★cmd-po_1203(T3-a): raw Modal frame → 共通外殻 PoModalShell / footer Button → PoButton
import { PoButton } from "./common/PoButton";
import { PoModalShell } from "./common/PoModalShell";
import { styled } from "@mui/system";

// jotai
import { useAtomValue } from "jotai";
import { AppAtoms } from "../lib/store";

// dnd
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvided,
  DraggableStateSnapshot,
} from "react-beautiful-dnd";

const CustomTextField = styled(TextField)({
  "& .MuiInputBase-input": {
    fontSize: "12px",
    height: "12px",
    color: "var(--color-sumi, #374151)",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "var(--radius-sm, 4px)",
    "& fieldset": {
      borderColor: "var(--color-border, #e5e5e0)",
    },
  },
});
CustomTextField.defaultProps = {
  autoComplete: "off",
};

interface ColumnSettingModalProps {
  onModal: boolean;
  swCloseModal: () => void;
  columnSetting: {
    status: "none" | "open" | "loading" | "result" | "error";
    columns: {
      id: number;
      screen_column_auth: 0 | 1;
      screen_column_system_default: 0 | 1;
      table_disabled: 0 | 1;
      screen_column_serch_disabled: 0 | 1;
      detail_disabled: 0 | 1;
      screen_column_name: string;
      screen_column_type: 0 | 1 | 2;
      table_sort: 0 | 1;
      screen_column_table: 0 | 1;
      table_column_size: string;
      screen_column_sort_id: number;
      screen_column_serch: 0 | 1;
      screen_column_detail: 0 | 1;
    }[];
  };
  setColumnSetting: (status: {
    status: "none" | "open" | "loading" | "result" | "error";
    columns: {
      id: number;
      screen_column_auth: 0 | 1;
      screen_column_system_default: 0 | 1;
      table_disabled: 0 | 1;
      screen_column_serch_disabled: 0 | 1;
      detail_disabled: 0 | 1;
      screen_column_name: string;
      screen_column_type: 0 | 1 | 2;
      table_sort: 0 | 1;
      screen_column_table: 0 | 1;
      table_column_size: string;
      screen_column_sort_id: number;
      screen_column_serch: 0 | 1;
      screen_column_detail: 0 | 1;
    }[];
  }) => void;
  putClumnSetting: () => void;
}

export function ColumnSettingModal(props: ColumnSettingModalProps) {
  const {
    onModal,
    swCloseModal,
    columnSetting,
    setColumnSetting,
    putClumnSetting,
  } = props;
  // ★cmd-po_1203: 旧 frame style 定数(760px/92vh/radius/shadow)は PoModalShell width="w760"(PO_MODAL_SX_W760)へ移譲＝除去。
  const pathname = useAtomValue(AppAtoms.pathname);

  // ドラッグが完了した際に呼ばれるコールバック
  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    // ドロップ先がない(リスト外にドロップされた)場合は何もしない
    if (!destination) return;
    if (pathname === "/runking") return;

    // 元の場所と同じ場合は並び替え不要
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    // 配列をコピーし、ドラッグされた要素を抜き出し、目的地へ挿入
    const newColumns = Array.from(columnSetting.columns);
    const [removed] = newColumns.splice(source.index, 1);
    newColumns.splice(destination.index, 0, removed);

    // 並び替えが完了した後、sort_id を並び順に合わせて更新する
    // (1スタートの場合は index+1、0スタートなら index を使ってください)
    newColumns.forEach((option: any, index: number) => {
      option.screen_column_sort_id = index + 1;
    });
    // 更新した配列をStateに反映させる
    setColumnSetting({
      ...columnSetting,
      columns: newColumns,
    });
  };

  return (
    // ★cmd-po_1203(T3-a): raw Modal frame+手組みheader → 共通外殻 PoModalShell(w760)（precedent=cmd-po_1200 双子/1202 CopyTossUp）
    //   3 status(open/error/result)を body=children・footer=footer prop に分離／×(column-setting-x-button)は shell派生×へ移設(新設でない・onClick=swCloseModal)
    //   footer Button→PoButton整合(cancel/close=secondary・submit=primary)／error・result close の固定px(w160/h40/fs20)→auto幅正規化
    //   ★backdrop/Escで閉じない現挙動保全（旧Modalに onClose 無配線=PoModalShell も同=差異なし）／bodySx p:0=body内側padding温存(移設のみ)
    <PoModalShell
      open={onModal}
      title="項目一覧の変更"
      onClose={swCloseModal}
      width="w760"
      testidPrefix="column-setting"
      frameTestId="column-setting-modal"
      bodySx={{ p: 0 }}
      footer={
        columnSetting.status === "open" ? (
          <>
            <PoButton
              data-testid="column-setting-cancel"
              variant="secondary"
              onClick={() => {
                swCloseModal();
              }}
            >
              キャンセル
            </PoButton>
            <PoButton
              data-testid="column-setting-submit"
              variant="primary"
              onClick={() => {
                putClumnSetting();
              }}
            >
              項目更新
            </PoButton>
          </>
        ) : columnSetting.status === "error" ? (
          <PoButton
            data-testid="column-setting-error-close"
            variant="secondary"
            onClick={swCloseModal}
          >
            閉じる
          </PoButton>
        ) : columnSetting.status === "result" ? (
          <PoButton
            data-testid="column-setting-success-close"
            variant="secondary"
            onClick={() => {
              swCloseModal();
            }}
          >
            閉じる
          </PoButton>
        ) : undefined
      }
    >
        {columnSetting.status === "open" ? (
          <Typography id="modal-modal-description" sx={{ mt: 0, mb: 0, flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Box sx={{ pt: "var(--sp-3)", px: "var(--sp-5)", pb: "var(--sp-4)", flex: 1, overflowY: "auto" }}>
              <TableContainer
                component={Paper}
                sx={{
                  margin: "1px 1px",
                  width: "calc(100% - 4px)",
                  maxHeight: "calc(70vh - 80px)",
                  border: "1px solid rgb(230, 230, 230)",
                }}
              >
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="droppable">
                    {(provided) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{ width: "100%" }}
                      >
                        <Table
                          stickyHeader
                          size="small"
                          aria-label="collapsible table"
                        >
                          <TableHead>
                            <TableRow key="detaiTableRowHead">
                              <TableCell
                                key={"tableColumnKomoku0"}
                                sx={{
                                  width: "24px",
                                  minWidth: "24px",
                                  padding: "0 4px",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "var(--color-white)",
                                  backgroundColor: "var(--color-ai)",
                                  borderBottom: "1px solid var(--color-border, #e5e5e0)",
                                }}
                              />
                              <TableCell
                                key={"tableColumnKomoku1"}
                                align="center"
                                sx={{
                                  width: "30%",
                                  minWidth: "100px",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "var(--color-white)",
                                  backgroundColor: "var(--color-ai)",
                                  borderBottom: "1px solid var(--color-border, #e5e5e0)",
                                }}
                              >
                                項目名
                              </TableCell>
                              <TableCell
                                key={"tableColumnKomoku2"}
                                align="center"
                                sx={{
                                  width: "25%",
                                  minWidth: "70px",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "var(--color-white)",
                                  backgroundColor: "var(--color-ai)",
                                  borderBottom: "1px solid var(--color-border, #e5e5e0)",
                                }}
                              >
                                項目サイズ
                              </TableCell>
                              <TableCell
                                key={"tableColumnKomoku3"}
                                align="center"
                                sx={{
                                  width: "15%",
                                  minWidth: "70px",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "var(--color-white)",
                                  backgroundColor: "var(--color-ai)",
                                  borderBottom: "1px solid var(--color-border, #e5e5e0)",
                                }}
                              >
                                一覧表示
                              </TableCell>
                              <TableCell
                                key={"tableColumnKomoku4"}
                                align="center"
                                sx={{
                                  width: "15%",
                                  minWidth: "70px",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "var(--color-white)",
                                  backgroundColor: "var(--color-ai)",
                                  borderBottom: "1px solid var(--color-border, #e5e5e0)",
                                }}
                              >
                                検索表示
                              </TableCell>
                              <TableCell
                                key={"tableColumnKomoku5"}
                                align="center"
                                sx={{
                                  width: "15%",
                                  minWidth: "70px",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "var(--color-white)",
                                  backgroundColor: "var(--color-ai)",
                                  borderBottom: "1px solid var(--color-border, #e5e5e0)",
                                }}
                              >
                                詳細表示
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody key={"detaiTableRowBody"}>
                            {columnSetting.columns.map((column, index) => {
                              return (
                                <Draggable
                                  key={column.screen_column_sort_id}
                                  draggableId={String(
                                    column.screen_column_sort_id,
                                  )}
                                  index={index}
                                  isDragDisabled={pathname === "/runking"}
                                >
                                  {(
                                    provided: DraggableProvided,
                                    snapshot: DraggableStateSnapshot,
                                  ) => (
                                    <>
                                      <Fragment
                                        key={"detaiTableRowBody" + index}
                                      >
                                        <TableRow
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          sx={{
                                            backgroundColor: snapshot.isDragging
                                              ? "var(--color-ai-pale)"
                                              : "var(--color-white)",
                                          }}
                                        >
                                          <TableCell
                                            key={
                                              "detaiTableRowBodyKomoku0_" +
                                              index
                                            }
                                            {...provided.dragHandleProps}
                                            sx={{
                                              width: "24px",
                                              padding: "0 4px",
                                              fontSize: 13,
                                              color: "var(--color-sumi)",
                                              cursor: "grab",
                                              borderBottom: "1px solid var(--color-border-light, #efefea)",
                                            }}
                                          >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                              <circle cx="9" cy="6" r="1.4"/>
                                              <circle cx="15" cy="6" r="1.4"/>
                                              <circle cx="9" cy="12" r="1.4"/>
                                              <circle cx="15" cy="12" r="1.4"/>
                                              <circle cx="9" cy="18" r="1.4"/>
                                              <circle cx="15" cy="18" r="1.4"/>
                                            </svg>
                                          </TableCell>
                                          <TableCell
                                            key={
                                              "detaiTableRowBodyKomoku1_" +
                                              index
                                            }
                                            align="center"
                                            sx={{ fontSize: 13, color: "var(--color-sumi, #374151)", borderBottom: "1px solid var(--color-border-light, #efefea)" }}
                                          >
                                            {column.screen_column_name}
                                          </TableCell>
                                          <TableCell
                                            key={
                                              "detaiTableRowBodyKomoku2_" +
                                              index
                                            }
                                            align="center"
                                            sx={{ fontSize: 13, color: "var(--color-sumi, #374151)", borderBottom: "1px solid var(--color-border-light, #efefea)" }}
                                          >
                                            <CustomTextField
                                              disabled={
                                                pathname === "/runking"
                                              }
                                              id="textFieldId"
                                              size="small"
                                              value={column.table_column_size}
                                              onChange={async (e) => {
                                                const inputValue =
                                                  e.target.value;
                                                if (
                                                  /^\d*$/.test(inputValue)
                                                ) {
                                                  let _columns =
                                                    columnSetting.columns.concat();
                                                  _columns[index][
                                                    "table_column_size"
                                                  ] = inputValue;
                                                  setColumnSetting({
                                                    ...columnSetting,
                                                    columns: _columns,
                                                  });
                                                }
                                              }}
                                            />
                                          </TableCell>
                                          <TableCell
                                            key={
                                              "detaiTableRowBodyKomoku3_" +
                                              index
                                            }
                                            align="center"
                                            sx={{ fontSize: 13, color: "var(--color-sumi, #374151)", borderBottom: "1px solid var(--color-border-light, #efefea)" }}
                                          >
                                            <Checkbox
                                              sx={{
                                                "& .MuiSvgIcon-root": {
                                                  fontSize: 16,
                                                },
                                                marginTop: "-5px",
                                              }}
                                              disabled={
                                                column.table_disabled === 1
                                              }
                                              checked={
                                                column.screen_column_table ===
                                                1
                                              }
                                              onClick={() => {
                                                let _columns =
                                                  columnSetting.columns.concat();
                                                if (
                                                  column.screen_column_table ===
                                                  1
                                                ) {
                                                  _columns[index][
                                                    "screen_column_table"
                                                  ] = 0;
                                                } else {
                                                  _columns[index][
                                                    "screen_column_table"
                                                  ] = 1;
                                                }
                                                setColumnSetting({
                                                  ...columnSetting,
                                                  columns: _columns,
                                                });
                                              }}
                                            />
                                          </TableCell>
                                          <TableCell
                                            key={
                                              "detaiTableRowBodyKomoku4_" +
                                              index
                                            }
                                            align="center"
                                            sx={{ fontSize: 13, color: "var(--color-sumi, #374151)", borderBottom: "1px solid var(--color-border-light, #efefea)" }}
                                          >
                                            <Checkbox
                                              sx={{
                                                "& .MuiSvgIcon-root": {
                                                  fontSize: 16,
                                                },
                                                marginTop: "-5px",
                                              }}
                                              disabled={
                                                column.screen_column_serch_disabled ===
                                                1
                                              }
                                              checked={
                                                column.screen_column_serch ===
                                                1
                                              }
                                              onClick={() => {
                                                let _columns =
                                                  columnSetting.columns.concat();
                                                if (
                                                  column.screen_column_serch ===
                                                  1
                                                ) {
                                                  _columns[index][
                                                    "screen_column_serch"
                                                  ] = 0;
                                                } else {
                                                  _columns[index][
                                                    "screen_column_serch"
                                                  ] = 1;
                                                }
                                                setColumnSetting({
                                                  ...columnSetting,
                                                  columns: _columns,
                                                });
                                              }}
                                            />
                                          </TableCell>
                                          <TableCell
                                            key={
                                              "detaiTableRowBodyKomoku5_" +
                                              index
                                            }
                                            align="center"
                                            sx={{ fontSize: 13, color: "var(--color-sumi, #374151)", borderBottom: "1px solid var(--color-border-light, #efefea)" }}
                                          >
                                            <Checkbox
                                              sx={{
                                                "& .MuiSvgIcon-root": {
                                                  fontSize: 16,
                                                },
                                                marginTop: "-5px",
                                              }}
                                              disabled={
                                                column.detail_disabled === 1
                                              }
                                              checked={
                                                column.screen_column_detail ===
                                                1
                                              }
                                              onClick={() => {
                                                let _columns =
                                                  columnSetting.columns.concat();
                                                if (
                                                  column.screen_column_detail ===
                                                  1
                                                ) {
                                                  _columns[index][
                                                    "screen_column_detail"
                                                  ] = 0;
                                                } else {
                                                  _columns[index][
                                                    "screen_column_detail"
                                                  ] = 1;
                                                }
                                                setColumnSetting({
                                                  ...columnSetting,
                                                  columns: _columns,
                                                });
                                              }}
                                            />
                                          </TableCell>
                                        </TableRow>
                                      </Fragment>
                                    </>
                                  )}
                                </Draggable>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              </TableContainer>
              <Box sx={{ fontSize: 11, color: "var(--color-sumi-muted, #9ca3af)", mt: "var(--sp-2, 8px)" }}>
                行をドラッグして表示順を並び替えできます。灰色のチェックは変更できない項目です。「検索表示」は右側検索パネルの表示項目と連動します。
              </Box>
            </Box>
          </Typography>
        ) : columnSetting.status === "error" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 0, flexShrink: 0, px: 3, pb: 3 }}>
            <Alert severity="error" data-testid="column-setting-alert">
              <Box>項目の更新に失敗しました。</Box>
            </Alert>
          </Typography>
        ) : columnSetting.status === "result" ? (
          <Typography id="modal-modal-description" sx={{ mt: 2, mb: 0, flexShrink: 0, px: 3, pb: 3 }}>
            <Box>項目の更新に成功しました。</Box>
          </Typography>
        ) : null}
    </PoModalShell>
  );
}
